/**
 * Enhanced Excel Export Utility for Financial Data
 *
 * Handles:
 * - Percentages: 5.2%, (3.5%), -2.1%
 * - Currency: $1,234.56, ($500), $2.5M, $1.2B
 * - Multipliers: 8.5x, 90bp, 90bps
 * - Plain numbers: 1,234, (500)
 *
 * All values exported as proper Excel numbers with formatting
 */

import * as XLSX from 'xlsx';

interface Block {
  id: string;
  block_type: string;
  text?: string;
  html?: string;
  children?: Block[];
  bbox?: number[];
  structured_data?: {
    headers?: string[];
    rows?: string[][];
    [key: string]: any;
  };
}

interface ParsedCell {
  value: number | string;
  format?: string;
}

/**
 * Parse financial cell value with format detection
 */
export function parseFinancialCell(text: string): ParsedCell {
  const cleaned = text.trim();

  // Empty, dash, or N/A
  if (!cleaned || cleaned === '-' || cleaned === '—' || cleaned === 'N/A' || cleaned === 'n/a' || cleaned === 'NA') {
    return { value: '' };
  }

  // Percentage: "5.2%", "(3.5%)", "-2.1%", "- 2.1%"
  const pctMatch = cleaned.match(/^\(?([-−]?\s*[\d,]+\.?\d*)\s*\)?%$/);
  if (pctMatch) {
    const numStr = pctMatch[1].replace(/,/g, '').replace(/−/g, '-').replace(/\s/g, '');
    const num = parseFloat(numStr);
    const isNegative = cleaned.startsWith('(') || num < 0;
    return {
      value: (isNegative ? -Math.abs(num) : num) / 100,
      format: '0.0%'
    };
  }

  // Currency with multiplier: "$2.5M", "$1.2B", "$500K", "($2.5M)"
  const currMultMatch = cleaned.match(/^\$?\(?([-−]?[\d,]+\.?\d*)\s*(M|B|K|m|b|k)\)?$/);
  if (currMultMatch) {
    let num = parseFloat(currMultMatch[1].replace(/,/g, '').replace(/−/g, '-'));
    const multiplier: Record<string, number> = { 'K': 1e3, 'k': 1e3, 'M': 1e6, 'm': 1e6, 'B': 1e9, 'b': 1e9 };
    num *= multiplier[currMultMatch[2]] || 1;
    const isNegative = cleaned.includes('(') || num < 0;
    return {
      value: isNegative ? -Math.abs(num) : num,
      format: '#,##0.0,,"M"'
    };
  }

  // Currency: "$1,234.56", "($500)", "-$1,234"
  const currMatch = cleaned.match(/^\$?\(?-?([-−]?[\d,]+\.?\d*)\)?$/);
  if (currMatch && cleaned.includes('$')) {
    const num = parseFloat(currMatch[1].replace(/,/g, '').replace(/−/g, '-'));
    const isNegative = cleaned.includes('(') || cleaned.startsWith('-$') || num < 0;
    return {
      value: isNegative ? -Math.abs(num) : num,
      format: '$#,##0.00'
    };
  }

  // Multiplier: "8.5x", "10.2x"
  const xMatch = cleaned.match(/^\(?([-−]?[\d,]+\.?\d*)\s*x\)?$/i);
  if (xMatch) {
    const num = parseFloat(xMatch[1].replace(/,/g, '').replace(/−/g, '-'));
    const isNegative = cleaned.includes('(') || num < 0;
    return {
      value: isNegative ? -Math.abs(num) : num,
      format: '0.0"x"'
    };
  }

  // Basis points: "90bp", "90bps", "(50bp)"
  const bpMatch = cleaned.match(/^\(?([-−]?[\d,]+\.?\d*)\s*(?:bp|bps)\)?$/i);
  if (bpMatch) {
    const num = parseFloat(bpMatch[1].replace(/,/g, '').replace(/−/g, '-'));
    const isNegative = cleaned.includes('(') || num < 0;
    return {
      value: isNegative ? -Math.abs(num) : num,
      format: '0"bp"'
    };
  }

  // Plain number with parentheses: "(500)", "1,234", "-500"
  const numMatch = cleaned.match(/^\(?([-−]?[\d,]+\.?\d*)\)?$/);
  if (numMatch) {
    const numStr = numMatch[1].replace(/,/g, '').replace(/−/g, '-');
    // Only parse as number if it's actually a number (not just digits that might be codes)
    if (/^-?\d+\.?\d*$/.test(numStr)) {
      const num = parseFloat(numStr);
      const isNegative = cleaned.startsWith('(') || num < 0;
      // Determine format based on decimal places
      const hasDecimals = numStr.includes('.');
      const decimalPlaces = hasDecimals ? numStr.split('.')[1]?.length || 0 : 0;
      return {
        value: isNegative ? -Math.abs(num) : num,
        format: hasDecimals ? `#,##0.${'0'.repeat(decimalPlaces)}` : '#,##0'
      };
    }
  }

  // Text (company names, dates, etc.)
  return { value: cleaned };
}

/**
 * Clean HTML to plain text
 */
function cleanText(str: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = str.replace(/<br\s*\/?>/gi, ' ');
  return (temp.textContent || '').replace(/\s+/g, ' ').trim();
}

/**
 * Extract rows from HTML table (most reliable method)
 */
function parseTableHTML(html: string): string[][] {
  const div = document.createElement('div');
  div.innerHTML = html;
  const table = div.querySelector('table');
  if (!table) return [];

  return Array.from(table.querySelectorAll('tr'))
    .map(tr => Array.from(tr.querySelectorAll('td, th'))
      .map(cell => cell.textContent?.trim() || '')
    )
    .filter(row => row.length > 0);
}

/**
 * Extract rows from TableRow children (fallback method)
 */
function parseTableRows(children: Block[]): string[][] {
  // Case-insensitive match for TableRow
  const tableRows = children.filter(c =>
    c.block_type?.toLowerCase() === 'tablerow'
  );
  if (!tableRows.length) return [];

  return tableRows.map(row =>
    (row.children || [])
      .sort((a, b) => (a.bbox?.[0] || 0) - (b.bbox?.[0] || 0))
      .map(cell => cleanText(cell.text || cell.html || ''))
  );
}

/**
 * Extract rows by grouping flat TableCell children by Y coordinate
 * This handles cases where cells aren't nested in TableRow elements
 */
function parseTableCellsByY(children: Block[]): string[][] {
  // Case-insensitive match for TableCell
  const cells = children.filter(c =>
    c.block_type?.toLowerCase() === 'tablecell'
  );
  if (!cells.length) return [];

  // Group cells by Y coordinate (rounded to handle minor variations)
  const byY: Record<number, Block[]> = {};
  cells.forEach(c => {
    if (!c.bbox) return;
    const y = Math.round(c.bbox[1]);
    (byY[y] = byY[y] || []).push(c);
  });

  // Sort rows by Y, then cells within each row by X
  return Object.keys(byY)
    .sort((a, b) => Number(a) - Number(b))
    .map(y => byY[Number(y)]
      .sort((a, b) => (a.bbox?.[0] || 0) - (b.bbox?.[0] || 0))
      .map(c => cleanText(c.text || c.html || ''))
    );
}

/**
 * Parse text content as table (tab/newline separated)
 */
function parseTextAsTable(text: string): string[][] {
  if (!text?.trim()) return [];

  // Split by newlines, then by tabs or multiple spaces
  const lines = text.split(/[\n\r]+/).filter(line => line.trim());
  if (lines.length === 0) return [];

  return lines.map(line => {
    // First try tab separation
    if (line.includes('\t')) {
      return line.split('\t').map(cell => cell.trim());
    }
    // Then try multiple spaces (3+)
    if (/\s{3,}/.test(line)) {
      return line.split(/\s{3,}/).map(cell => cell.trim());
    }
    // Single cell
    return [line.trim()];
  }).filter(row => row.some(cell => cell)); // Filter empty rows
}

/**
 * Recursively collect all children with bbox, group by Y
 */
function collectAllCellsByY(block: Block): string[][] {
  const allBlocks: Block[] = [];

  const collectBlocks = (b: Block) => {
    if (b.bbox && (b.text || b.html)) {
      allBlocks.push(b);
    }
    if (b.children) {
      b.children.forEach(child => collectBlocks(child));
    }
  };

  if (block.children) {
    block.children.forEach(child => collectBlocks(child));
  }

  if (allBlocks.length === 0) return [];

  // Group by Y coordinate with 5px tolerance
  const byY: Record<number, Block[]> = {};
  allBlocks.forEach(b => {
    if (!b.bbox) return;
    const y = Math.round(b.bbox[1] / 5) * 5; // Round to nearest 5
    (byY[y] = byY[y] || []).push(b);
  });

  return Object.keys(byY)
    .sort((a, b) => Number(a) - Number(b))
    .map(y => byY[Number(y)]
      .sort((a, b) => (a.bbox?.[0] || 0) - (b.bbox?.[0] || 0))
      .map(b => cleanText(b.text || b.html || ''))
    );
}

/**
 * Main enhanced export function - exports a table block to Excel
 * with proper financial number formatting
 */
export function exportTableToExcelEnhanced(block: Block, filename: string): void {
  let rawRows: string[][] = [];

  // Priority 0: structured_data (most reliable - already parsed)
  if (block.structured_data) {
    const sd = block.structured_data;
    if (sd.headers && sd.rows) {
      rawRows = [sd.headers, ...sd.rows];
      console.log('[ExcelExport] Method 0 (structured_data):', rawRows.length, 'rows');
    } else if (sd.rows) {
      rawRows = sd.rows;
      console.log('[ExcelExport] Method 0 (structured_data rows only):', rawRows.length, 'rows');
    }
  }

  // Priority 1: HTML table (most reliable for column alignment)
  if (!rawRows.length && block.html?.includes('<table')) {
    rawRows = parseTableHTML(block.html);
  }

  // Priority 2: TableRow children
  if (!rawRows.length && block.children) {
    rawRows = parseTableRows(block.children);
  }

  // Priority 3: Flat TableCell children grouped by Y coordinate
  if (!rawRows.length && block.children) {
    rawRows = parseTableCellsByY(block.children);
  }

  // Priority 4: Recursively collect all children by Y
  if (!rawRows.length && block.children) {
    rawRows = collectAllCellsByY(block);
  }

  // Priority 5: Parse text content as table
  if (!rawRows.length && block.text) {
    rawRows = parseTextAsTable(block.text);
  }

  // Priority 6: Parse cleaned HTML text as table
  if (!rawRows.length && block.html) {
    const text = cleanText(block.html);
    rawRows = parseTextAsTable(text);
  }

  // Priority 7: Try to build from any children structure
  if (!rawRows.length && block.children) {
    const rows: string[][] = [];

    // Try simple row-based extraction: each child is a row, its children are cells
    for (const child of block.children) {
      const rowTexts: string[] = [];
      if (child.children && child.children.length > 0) {
        // Child has nested children (likely cells)
        for (const cell of child.children) {
          const text = cleanText(cell.text || cell.html || '');
          if (text) rowTexts.push(text);
        }
      } else {
        // Child is a leaf node
        const text = cleanText(child.text || child.html || '');
        if (text) rowTexts.push(text);
      }
      if (rowTexts.length > 0) {
        rows.push(rowTexts);
      }
    }
    if (rows.length > 0) {
      rawRows = rows;
    }
  }

  if (!rawRows.length) {
    // Only log on error
    console.error('[ExcelExport] Failed to extract table. Block:', {
      id: block.id,
      type: block.block_type,
      hasText: !!block.text,
      hasHtml: !!block.html,
      hasStructuredData: !!block.structured_data,
      childrenCount: block.children?.length || 0
    });
    alert('Could not extract table data. Open Block Inspector (bug icon) to see block details.');
    return;
  }

  // Parse all cells with financial formatting
  const parsedRows: ParsedCell[][] = rawRows.map(row =>
    row.map(cell => parseFinancialCell(cell))
  );

  // Create worksheet data (just values)
  const wsData: (number | string)[][] = parsedRows.map(row =>
    row.map(cell => cell.value)
  );

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // Apply number formats to cells
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const cellRef = XLSX.utils.encode_cell({ r, c });
      const cell = ws[cellRef];
      const parsed = parsedRows[r]?.[c];

      if (cell && parsed?.format) {
        cell.z = parsed.format;
      }
    }
  }

  // Auto-size columns
  const widths: number[] = [];
  rawRows.forEach(row => {
    row.forEach((cell, i) => {
      const len = cell.length + 2;
      widths[i] = Math.max(widths[i] || 8, Math.min(len, 40));
    });
  });
  ws['!cols'] = widths.map(w => ({ wch: w }));

  // Create workbook and export
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Table');
  XLSX.writeFile(wb, filename);
}

// Re-export for compatibility
export { parseFinancialCell as parseCell };
